import { useEffect, useRef, useState } from 'react'

import { TextareaWithSubmit } from '@/components/composer/TextareaWithSubmit'
import { CreateEmailTemplateButton } from '@/components/CreateEmailTemplateButton'
import { EmailTemplateSelectItems } from '@/components/EmailTemplateSelectItems'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useEmailTemplates } from '@/models/emailTemplate'

const SMS_SEGMENT_LEN = 160

// Mirrors the backend's SUPPORTED_TOKENS (insure/services/sequences/render.py).
const VARIABLES = [
  'lead.first_name',
  'lead.contact_name',
  'lead.company',
  'lead.email',
  'lead.phone',
  'lead.dot_number',
  'rep.first_name',
  'rep.name',
  'rep.email',
]

interface SmsStepEditorProps {
  body: string
  onChange: (body: string) => void
  onSave: () => void
  onCancel: () => void
  saving?: boolean
  saveLabel?: string
  // New (empty) step: prefill from the first template (no-op when the rep has
  // no templates yet — the step starts blank).
  prefill?: boolean
}

// SMS shares the email templates: an SMS step just uses a template's plain-text
// body as its message (subject/HTML don't apply). The composer mirrors the
// lead-activity SMS reply (plain textarea + char/segment counter), not the rich
// email editor.
export function SmsStepEditor({
  body,
  onChange,
  onSave,
  onCancel,
  saving,
  saveLabel = 'Add step',
  prefill,
}: SmsStepEditorProps) {
  const { data: templates = [], isSuccess: templatesLoaded } = useEmailTemplates()
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
  const prefilled = useRef(false)

  const segments = body.length === 0 ? 0 : Math.ceil(body.length / SMS_SEGMENT_LEN)

  function applyTemplate(id: string) {
    const t = templates.find((x) => x.id === id)
    if (!t) return
    setSelectedTemplateId(id)
    onChange(t.body)
  }

  // Prefill a new step from the default template once templates have loaded, as
  // long as the rep hasn't started typing. Templates come sorted default-first.
  useEffect(() => {
    if (!prefill || prefilled.current || !templatesLoaded) return
    if (body !== '') return
    prefilled.current = true
    if (templates.length > 0) {
      applyTemplate(templates[0].id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill, templatesLoaded])

  function insertVariable(v: string) {
    onChange(`${body}{{${v}}}`)
  }

  return (
    <div className="flex flex-col gap-2 rounded-md border p-3">
      <div>
        <label className="mb-1 block text-sm font-medium">Template</label>
        {templates.length > 0 ? (
          <Select value={selectedTemplateId ?? undefined} onValueChange={applyTemplate}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Start from a template" />
            </SelectTrigger>
            <SelectContent>
              <EmailTemplateSelectItems templates={templates} />
            </SelectContent>
          </Select>
        ) : (
          <CreateEmailTemplateButton />
        )}
      </div>

      <div className="flex flex-wrap gap-1">
        {VARIABLES.map((v) => (
          <Button key={v} type="button" variant="outline" size="xs" onClick={() => insertVariable(v)}>
            {`{{${v}}}`}
          </Button>
        ))}
      </div>

      <TextareaWithSubmit
        value={body}
        onValueChange={onChange}
        onSubmitMessage={() => onSave()}
        placeholder="Compose SMS… use variables like {{lead.first_name}}"
        buttonProps={{ disabled: saving, 'aria-label': saveLabel }}
        addonChildren={
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
        }
        leadingHint={
          <>
            {body.length} chars
            {segments > 0 && (
              <>
                {' · '}
                {segments} segment{segments === 1 ? '' : 's'}
              </>
            )}
          </>
        }
      />
    </div>
  )
}
